import { useEffect, useMemo, useState } from 'react';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { selectHiddenClassOverrides } from '../redux/reducers/hiddenClassesSlice';
import { allClassesBasicData, classesByTeacherIdOnlyClasses } from '../graphQLOperations/Classes';
import { ClassesGetByTeacherIdOnlyClassesQuery } from '../graphQLOperations/__generated__/ClassesGetByTeacherIdOnlyClassesQuery.graphql';
import { ClassesGetAllBasicDataQuery } from '../graphQLOperations/__generated__/ClassesGetAllBasicDataQuery.graphql';
import { userHiddenClasses } from '../graphQLOperations/ClassCategories';
import { ClassCategoriesUserHiddenClassesQuery } from '../graphQLOperations/__generated__/ClassCategoriesUserHiddenClassesQuery.graphql';
import { isUserAdmin } from '../businessLogicService/isUserAdmin';
import { focusMembership } from '../graphQLOperations/Focus';
import { FocusMembershipQuery } from '../graphQLOperations/__generated__/FocusMembershipQuery.graphql';
import { bucketStudentsByRoadmap } from '../components/TeacherDashboard/unitFocusBuckets';
import { getTargetStudentIsWorkingOn } from '../graphQLOperations/SingleUnit';
import { SingleUnitTargetStudentIsWorkingOnQuery } from '../graphQLOperations/__generated__/SingleUnitTargetStudentIsWorkingOnQuery.graphql';
import { mapStudentCurrentUnit } from '../businessLogicService/focusService';

export interface FocusClassOption {
  id: string;
  label: string;
  studentCount: number;
}

function formatFocusClassLabel(c: { name?: string | null; section?: string | null }): string {
  return [c.section?.trim(), c.name?.trim()].filter(Boolean).join(' · ');
}

const ADMIN_CLASS_PAGE_SIZE = 5000;

type FocusClassRow = {
  id: string;
  name?: string | null;
  section?: string | null;
  students?: ReadonlyArray<{ readonly id: string } | null | undefined> | null;
};

function toFocusOptions(rows: ReadonlyArray<FocusClassRow | null | undefined> | null | undefined): FocusClassOption[] {
  return (rows ?? [])
    .filter((c): c is FocusClassRow => !!c?.id)
    .map(c => ({ id: c.id, label: formatFocusClassLabel(c), studentCount: c.students?.length ?? 0 }));
}

export function useTeacherClasses(open: boolean): { classes: FocusClassOption[]; loading: boolean } {
  const environment = useRelayEnvironment();
  const user = useAppSelector(selectUser);
  const hiddenOverrides = useAppSelector(selectHiddenClassOverrides);
  const [rawClasses, setRawClasses] = useState<FocusClassOption[]>([]);
  const [hiddenClassIds, setHiddenClassIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) {
      return;
    }

    let cancelled = false;
    setLoading(true);

    const filter = { haveAtLeastOneStudent: null, isActive: 'ACTIVE' as const, siteIsActive: 'ACTIVE' as const };
    const fetchPolicy = 'store-or-network' as const;

    const rows$: Promise<ReadonlyArray<FocusClassRow | null | undefined> | null | undefined> = isUserAdmin(user)
      ? fetchQuery<ClassesGetAllBasicDataQuery>(
          environment,
          allClassesBasicData,
          { filter, cursor: null, first: ADMIN_CLASS_PAGE_SIZE },
          { fetchPolicy },
        )
          .toPromise()
          .then(data => data?.classes?.nodes)
      : fetchQuery<ClassesGetByTeacherIdOnlyClassesQuery>(
          environment,
          classesByTeacherIdOnlyClasses,
          { filter, teacherId: `UserNode:${user.id}` },
          { fetchPolicy },
        )
          .toPromise()
          .then(data => data?.classesByTeacherId);

    const hiddenIds$ = fetchQuery<ClassCategoriesUserHiddenClassesQuery>(
      environment,
      userHiddenClasses,
      { publicUserId: user.sub ?? '', skip: !user.sub },
      { fetchPolicy },
    )
      .toPromise()
      .then(
        data =>
          new Set(
            (data?.userByPublicUserId?.userClassCategories ?? [])
              .filter(category => category?.category === 'HIDDEN')
              .map(category => category?.classId)
              .filter((classId): classId is string => !!classId),
          ),
      );

    Promise.all([rows$, hiddenIds$])
      .then(([rows, hiddenIds]) => {
        if (cancelled) return;
        setRawClasses(toFocusOptions(rows));
        setHiddenClassIds(hiddenIds);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setRawClasses([]);
          setHiddenClassIds(new Set());
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, user?.id, user?.sub, environment]);

  // Session overrides win so a hide/unhide in this session shows immediately,
  // without waiting on the (possibly stale) cached hidden-classes query.
  const classes = useMemo(() => {
    const hidden = new Set(hiddenClassIds);
    Object.entries(hiddenOverrides ?? {}).forEach(([classId, isHidden]) =>
      isHidden ? hidden.add(classId) : hidden.delete(classId),
    );
    return rawClasses.filter(c => !hidden.has(c.id));
  }, [rawClasses, hiddenClassIds, hiddenOverrides]);

  return { classes, loading };
}

export function useEligibleFocusClasses(
  open: boolean,
  roadmapId: string,
): { classes: FocusClassOption[]; loading: boolean } {
  const user = useAppSelector(selectUser);
  const admin = isUserAdmin(user);
  const { classes, loading } = useTeacherClasses(open);
  const membership = useRoadmapMembership(
    open && !admin && classes.length > 0,
    classes.map(c => c.id),
    roadmapId,
  );

  const filtered = useMemo(
    () => classes.filter(c => (membership.memberIdsByClass.get(c.id)?.size ?? 0) > 0),
    [classes, membership.memberIdsByClass],
  );

  if (admin || membership.error) return { classes, loading };
  return { classes: filtered, loading: loading || membership.loading };
}

export interface RoadmapMembership {
  memberIds: Set<string>;
  memberIdsByClass: ReadonlyMap<string, ReadonlySet<string>>;
  loading: boolean;
  error: boolean;
}

const EMPTY_MEMBERSHIP: RoadmapMembership = {
  memberIds: new Set(),
  memberIdsByClass: new Map(),
  loading: false,
  error: false,
};

export function useRoadmapMembership(visible: boolean, classIds: string[], roadmapId: string): RoadmapMembership {
  const environment = useRelayEnvironment();
  const [state, setState] = useState<RoadmapMembership>(EMPTY_MEMBERSHIP);
  const classKey = classIds.join(',');

  useEffect(() => {
    const ids = classKey ? classKey.split(',') : [];
    if (!visible || !roadmapId || ids.length === 0) {
      setState(EMPTY_MEMBERSHIP);
      return;
    }

    let cancelled = false;
    setState(prev => ({ ...prev, loading: true, error: false }));

    Promise.allSettled(
      ids.map(classId =>
        fetchQuery<FocusMembershipQuery>(
          environment,
          focusMembership,
          { classId, validCurrentRoadmapsOnly: true },
          { fetchPolicy: 'store-or-network' },
        ).toPromise(),
      ),
    ).then(results => {
      if (cancelled) return;
      if (results.some(result => result.status !== 'fulfilled')) {
        setState({ memberIds: new Set(), memberIdsByClass: new Map(), loading: false, error: true });
        return;
      }
      const memberIds = new Set<string>();
      const memberIdsByClass = new Map<string, ReadonlySet<string>>();
      results.forEach((result, i) => {
        if (result.status !== 'fulfilled') return;
        const bucket = bucketStudentsByRoadmap(result.value?.class?.users).find(b => b.id === roadmapId);
        const classMembers = new Set(bucket?.studentIds ?? []);
        classMembers.forEach(id => memberIds.add(id));
        memberIdsByClass.set(ids[i], classMembers);
      });
      setState({ memberIds, memberIdsByClass, loading: false, error: false });
    });

    return () => {
      cancelled = true;
    };
  }, [visible, classKey, roadmapId, environment]);

  return state;
}

export interface StudentsCurrentUnits {
  unitsByStudent: Map<string, { id: string; name: string }>;
  loading: boolean;
}

const EMPTY_STUDENT_UNITS: StudentsCurrentUnits = { unitsByStudent: new Map(), loading: false };

export function useStudentsCurrentUnits(visible: boolean, studentIds: string[]): StudentsCurrentUnits {
  const environment = useRelayEnvironment();
  const [state, setState] = useState<StudentsCurrentUnits>(EMPTY_STUDENT_UNITS);
  const idsKey = studentIds.join(',');

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : [];
    if (!visible || ids.length === 0) {
      setState(EMPTY_STUDENT_UNITS);
      return;
    }

    let cancelled = false;
    setState(prev => ({ ...prev, loading: true }));

    Promise.allSettled(
      ids.map(userId =>
        fetchQuery<SingleUnitTargetStudentIsWorkingOnQuery>(environment, getTargetStudentIsWorkingOn, {
          userId,
        }).toPromise(),
      ),
    ).then(results => {
      if (cancelled) return;
      const unitsByStudent = new Map<string, { id: string; name: string }>();
      results.forEach((result, i) => {
        if (result.status !== 'fulfilled') return;
        const unit = mapStudentCurrentUnit(result.value?.getTargetStudentIsWorkingOn);
        if (unit) unitsByStudent.set(ids[i], unit);
      });
      setState({ unitsByStudent, loading: false });
    });

    return () => {
      cancelled = true;
    };
  }, [visible, idsKey, environment]);

  return state;
}
